import { Flex, RadioGroup, RadioGroupItem, Text } from "@radix-ui/themes";
import { on } from "events";
import React, { useEffect, useState } from "react";

interface Option {
  answerId: string;
  value: string;
  isAnswer: string | boolean;
  scale_point: string;
}

interface LinearScaleProps {
  onChoice: (option: Option, index: number) => void;
  question: string;
  options: Option[];
  isMandatory: boolean;
  index: number;
  onValidate?: (index: number, isValid: boolean) => void;
}

const LinearScale: React.FC<LinearScaleProps> = ({
  question,
  options,
  isMandatory,
  index,
  onChoice,
  onValidate,
}) => {
  // Find the initially selected option (if any)
  const initialValue = options.find((option) => option.isAnswer)?.scale_point || '';
  const [selectedValue, setSelectedValue] = React.useState<string>(initialValue);

  const handleOptionChange = (value: string) => {
    const selectedOption = options.find((option) => option.scale_point === value);
    if (selectedOption) {
      setSelectedValue(value);
      onChoice(selectedOption, index);

      if (isMandatory && onValidate) {
        onValidate(index, true);
      }
    }
  };

  useEffect(() => {
    if (onValidate) {
      if (isMandatory) {
        onValidate(index, !!selectedValue);
      } else {
        onValidate(index, true); // Non-mandatory questions are always valid
      }
    }
  }, [selectedValue, isMandatory, index, onValidate]);

  return (
    <Flex direction="column" gap="2">
      <Text className="block mb-2 text-normal font-bold text-app-gray-medium" as="label" size="3">
        {question}
        {isMandatory && <span className="text-red-600">*</span>}
      </Text>
      <RadioGroup.Root
        value={selectedValue}
        onValueChange={handleOptionChange}
        color="purple"
        variant="classic"
      >
        <Flex direction="column" gap="2">
          {options.map((option) => (
            <Text key={option.answerId} as="label" className="my-auto">
              <Flex
                gap="2"
                className={
                  option.scale_point === selectedValue
                    ? "border-[0.5px] border-purple-600 rounded-xl shadow-lg py-[1.5%] px-[1%]"
                    : "border-[1px] rounded-xl py-[1%] px-[1%] shadow-sm"
                }
              >
                <RadioGroupItem
                  className="my-auto"
                  value={option.scale_point}
                  checked={option.scale_point === selectedValue}
                  onClick={() => handleOptionChange(option.scale_point)}
                />
                {option.value}
              </Flex>
            </Text>
          ))}
        </Flex>
      </RadioGroup.Root>
    </Flex>
  );
};

export default LinearScale;
